import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import { Card, Table, Input, Button, Typography, Tag } from "antd";
import { PlusOutlined, SearchOutlined, EditOutlined } from "@ant-design/icons";
import masterService from "../../../services/masterService";
import { useCommon } from "../../../context/CommonContext";

const { Title, Text } = Typography;
const { Search } = Input;

const MobileAppBuildConfigList = () => {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const notify = useNotify();
  // State management
  const [loading, setLoading] = useState(false);
  const [buildConfigs, setBuildConfigs] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) =>
      `${range[0]}-${range[1]} of ${total} build configs`,
  });

  // Filters
  const [filters, setFilters] = useState({
    searchText: "",
  });

  const getBuildConfigs = useCallback(
    async (params = {}) => {
      try {
        setLoading(true);

        const requestParams = {
          perPage: pagination.pageSize,
          currentPage: pagination.current,
          searchText: filters.searchText,
          ...params,
        };

        const response = await masterService.getMobileAppBuildConfigs(
          requestParams
        );

        if (response.data && response.data.status) {
          // Handle different possible response structures
          const buildConfigsData =
            response.data.data?.data || response.data.data || [];
          const paginationData =
            response.data.data?.pagination || response.data.pagination || {};

          if (Array.isArray(buildConfigsData)) {
            setBuildConfigs(buildConfigsData);
          } else {
            console.error("Build configs data is not an array:", buildConfigsData);
            setBuildConfigs([]);
          }

          setPagination((prev) => ({
            ...prev,
            ...paginationData,
            current: paginationData.currentPage || 1,
          }));
        } else {
          notify.error({ message: "Failed to fetch build configs" });
          setBuildConfigs([]);
        }
      } catch (error) {
        console.error("Error fetching build configs:", error);
        notify.error({ message: "Failed to fetch build configs" });
        setBuildConfigs([]);
      } finally {
        setLoading(false);
      }
    },
    [filters.searchText]
  );

  // Load build config data
  useEffect(() => {
    setPageTitle("Mobile App Build Config");
    getBuildConfigs();
  }, [getBuildConfigs, setPageTitle]);

  // Handle search
  const handleSearch = (value) => {
    setFilters((prev) => ({ ...prev, searchText: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    getBuildConfigs({
      searchText: value,
      perPage: pagination.pageSize,
      currentPage: 1,
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;

    setPagination((prev) => ({
      ...prev,
      current,
      pageSize,
    }));

    getBuildConfigs({
      searchText: filters.searchText,
      perPage: pageSize,
      currentPage: current,
    });
  };

  // Handle add build config
  const handleAddBuildConfig = () => {
    navigate("/mobile-app-build-config/add/new");
  };

  // Handle edit build config
  const handleEditBuildConfig = (buildConfigId) => {
    navigate(`/mobile-app-build-config/edit/${buildConfigId}`);
  };

  // Table columns
  const columns = [
    {
      title: "Build Number",
      dataIndex: "build_number",
      key: "build_number",
      render: (text) => (
        <Text strong style={{ fontSize: "14px" }}>
          {text}
        </Text>
      ),
    },
    {
      title: "Sales Channel",
      dataIndex: "sales_channel",
      key: "sales_channel",
      render: (salesChannel) => <Text>{salesChannel?.sc_name}</Text>,
    },
    {
      title: "Payment Gateway",
      dataIndex: "payment_gateway_enabled",
      key: "payment_gateway_enabled",
      width: 140,
      render: (enabled) => (
        <Tag color={enabled ? "green" : "red"}>
          {enabled ? "Enabled" : "Disabled"}
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 120,
      render: (status) => (
        <Tag color={status === "Y" ? "green" : "red"}>
          {status === "Y" ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      fixed: "right",
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditBuildConfig(record.build_config_id)}
          size="small"
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Card>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Mobile App Build Configs ({pagination.total})
        </Title>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            minWidth: 300,
            flex: 1,
            justifyContent: "flex-end",
          }}
        >
          <Search
            placeholder="Search by build number..."
            allowClear
            onSearch={handleSearch}
            style={{ width: 320, maxWidth: "40vw" }}
            enterButton={<SearchOutlined />}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddBuildConfig}
            size="middle"
          >
            Add Build Config
          </Button>
        </div>
      </div>

      <Text type="secondary" style={{ display: "block", marginBottom: "16px" }}>
        Only the builds listed here are restricted. A build with no entry keeps
        the payment gateway enabled.
      </Text>

      <Table
        columns={columns}
        dataSource={buildConfigs}
        rowKey="build_config_id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 800 }}
        size="middle"
        locale={{
          emptyText: "No build configs found",
          filterConfirm: "OK",
          filterReset: "Reset",
          filterEmptyText: "No filters",
        }}
      />
    </Card>
  );
};

export default MobileAppBuildConfigList;
